// Step 4 of the property wizard — "Xuất bản".
//
// Hosts the AI-quality stub, the publish status chip group, and a
// preview of the public share link. The share link is informational
// during create (the real slug is issued after publish); during edit
// it reflects the existing property id.

import { Compass } from "lucide-react";

import { AiQualityCheck } from "@/features/property/wizard/steps/ai-quality-check";
import {
  WizardChipPicker,
  WizardField,
} from "@/features/property/wizard/wizard-field";
import type { PropertyDraft } from "@/features/property/wizard/wizard-state";

type PublishStatus = NonNullable<PropertyDraft["status"]>;

interface StatusOption {
  value: PublishStatus;
  label: string;
}

const STATUS_OPTIONS_SALE: readonly StatusOption[] = [
  { value: "AVAILABLE", label: "Đang bán" },
  { value: "DEPOSIT", label: "Đã cọc" },
];

const STATUS_OPTIONS_RENT: readonly StatusOption[] = [
  { value: "AVAILABLE", label: "Đang cho thuê" },
  { value: "DEPOSIT", label: "Đã cọc" },
];

interface PublishStepProps {
  draft: PropertyDraft;
  onChange: (next: Partial<PropertyDraft>) => void;
  /** Set in the edit flow so the share link can render the real slug. */
  propertyId: number | undefined;
}

export function PublishStep({ draft, onChange, propertyId }: PublishStepProps) {
  const statusOptions =
    draft.listingType === "RENT" ? STATUS_OPTIONS_RENT : STATUS_OPTIONS_SALE;

  const slug =
    propertyId !== undefined ? `p/${propertyId}` : "p/new-property-slug";

  return (
    <div className="flex flex-col gap-6">
      <h2 className="border-b border-border pb-2 text-[16px] font-bold tracking-tight">
        Xuất bản
      </h2>

      <AiQualityCheck draft={draft} />

      <WizardField label="Trạng thái đăng tin">
        <WizardChipPicker
          ariaLabel="Trạng thái đăng tin"
          options={statusOptions}
          {...(draft.status !== undefined ? { value: draft.status } : {})}
          onChange={(v) => onChange({ status: v })}
        />
      </WizardField>

      <WizardField
        label="Link chia sẻ công khai"
        hint={
          propertyId === undefined
            ? "Link đầy đủ sẽ được tạo sau khi xuất bản."
            : undefined
        }
      >
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-[12px] text-muted-foreground">
          <Compass aria-hidden="true" className="size-3.5 shrink-0" />
          <span className="truncate">estate.vn/{slug}</span>
        </div>
      </WizardField>
    </div>
  );
}
